// Client-side annotation burn-in via pdf-lib: text, stamps, freehand
// drawing, and signature image embedding, committed onto a single page of
// the open document. Coordinates on elements are fractions (0..1) of the
// page's own, unrotated width/height — the caller captures them from a
// preview rendered at rotation 0, so no rotation math is needed here.
// Annotating a page that is currently displayed rotated in the viewer isn't
// supported in this first pass (documented limitation, not a bug).
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

export interface TextAnnotation {
  type: "text";
  id: string;
  xFrac: number;
  yFrac: number;
  text: string;
  color: string; // CSS hex, e.g. "#1d4ed8"
  fontSizeFrac: number; // font size as a fraction of page height
}

export interface PathAnnotation {
  type: "path";
  id: string;
  points: { x: number; y: number }[]; // fractions of page width/height
  color: string;
  lineWidthFrac: number; // stroke width as a fraction of page width
}

export type AnnotationElement = TextAnnotation | PathAnnotation;

const hexToRgb01 = (hex: string) => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex) ?? [];
  const n = (s?: string) => (s ? parseInt(s, 16) / 255 : 0);
  return rgb(n(m[1]), n(m[2]), n(m[3]));
};

/** Burn `elements` onto page `pageIndex` (0-based) of the document and return new bytes. */
export const applyAnnotationsToPage = async (
  bytes: Uint8Array,
  pageIndex: number,
  elements: AnnotationElement[],
): Promise<Uint8Array> => {
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const page = doc.getPages()[pageIndex];
  if (!page || !elements.length) return doc.save();
  const { width, height } = page.getSize();
  const font = await doc.embedFont(StandardFonts.Helvetica);

  for (const el of elements) {
    const color = hexToRgb01(el.color);
    if (el.type === "text") {
      const size = Math.max(6, el.fontSizeFrac * height);
      page.drawText(el.text, {
        x: el.xFrac * width,
        y: height - el.yFrac * height - size,
        size,
        font,
        color,
      });
    } else {
      const thickness = Math.max(1, el.lineWidthFrac * width);
      for (let i = 1; i < el.points.length; i++) {
        const a = el.points[i - 1];
        const b = el.points[i];
        page.drawLine({
          start: { x: a.x * width, y: height - a.y * height },
          end: { x: b.x * width, y: height - b.y * height },
          thickness,
          color,
        });
      }
    }
  }

  return doc.save();
};

// ── Signature embedding ───────────────────────────────────────────────────────

export interface SignaturePlacement {
  /** Left edge as a fraction of page width (0 = left edge). */
  xFrac: number;
  /** Top edge as a fraction of page height (0 = top). */
  yFrac: number;
  /** Signature width as a fraction of page width. Height preserves aspect ratio. */
  widthFrac: number;
}

/**
 * Embed a drawn signature (PNG data URL) onto a single page and return new
 * bytes. The image is placed at the fractional coordinates given by `opts`,
 * with height computed from the image's natural aspect ratio.
 */
export const embedSignatureOnPage = async (
  bytes: Uint8Array,
  pageIndex: number,
  pngDataUrl: string,
  opts: SignaturePlacement,
): Promise<Uint8Array> => {
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const page = doc.getPages()[pageIndex];
  if (!page) return doc.save();

  const { width, height } = page.getSize();

  // Decode the data URL with atob: `Buffer` only exists in Node, not the renderer.
  const base64 = pngDataUrl.replace(/^data:image\/png;base64,/, "");
  const raw = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const image = await doc.embedPng(raw);

  const imgW = opts.widthFrac * width;
  const imgH = imgW * (image.height / image.width);
  const x = opts.xFrac * width;
  // PDF y=0 is at the bottom; opts.yFrac is measured from the top.
  const y = height - opts.yFrac * height - imgH;

  page.drawImage(image, { x, y, width: imgW, height: imgH });
  return doc.save();
};
